/**
 * Identifiant qualifié (ADR-020 §1 ; 151 §1-§2 ; K43). La clé métier d'un `Identifier` est
 * `(identifier_type, value, qualifiants normalisés)` : un GTIN seul et un GTIN + lot sont deux
 * identifiants distincts, chacun lié à son objet canonique. « DPP ≠ GTIN » est tenu par le modèle.
 *
 * Fonctions pures, sans terme métier : les noms de qualifiants GS1 sont ceux du Syntax Dictionary
 * (AI 10, 21, 22, 235, 254, 7040), les combinaisons admises celles de l'attribut `dlpkey` de la clé.
 */
/** AI GS1 de chaque nom de qualifiant. */
export const GS1_QUALIFIER_AI = Object.freeze({
    cpv: '22',
    lot: '10',
    serial: '21',
    tpx: '235',
    extension: '254',
    uic_ext: '7040',
});
/** Nom du qualifiant d'un AI GS1, ou `undefined` si l'AI n'est pas un qualifiant connu. */
export function gs1QualifierName(ai) {
    return Object.entries(GS1_QUALIFIER_AI).find(([, code]) => code === ai)?.[0];
}
/**
 * Séquences de qualifiants admises par type d'identifiant GS1 (attribut `dlpkey` du Syntax
 * Dictionary) : une seule séquence à la fois, ordre de granularité croissante.
 */
export const GS1_QUALIFIER_SEQUENCES = Object.freeze({
    gtin: [['cpv', 'lot', 'serial'], ['tpx']],
    sscc: [],
    gln: [['extension'], ['uic_ext']],
    grai: [],
    giai: [['uic_ext']],
});
/** AI de la clé primaire GS1 d'un type d'identifiant. */
export const GS1_PRIMARY_AI = Object.freeze({ gtin: '01', sscc: '00', gln: '414', grai: '8003', giai: '8004' });
/** Type d'identifiant d'un AI de clé primaire GS1. */
export function identifierTypeOfPrimaryAi(ai) {
    return Object.entries(GS1_PRIMARY_AI).find(([, code]) => code === ai)?.[0];
}
const NAME = /^[a-z][a-z0-9_]*$/;
// CSET 82 (spécifications générales GS1 §7.11, figure 7.11-1).
const CSET82 = /^[!"%&'()*+,\-./0-9:;<=>?A-Z_a-z]+$/;
const MAX_LENGTH = { cpv: 20, lot: 20, serial: 20, tpx: 28, extension: 20 };
/** Qualifiants normalisés : noms triés, valeurs vides retirées, valeurs conservées telles quelles. */
export function normalizeQualifiers(qualifiers) {
    if (qualifiers === null || qualifiers === undefined || typeof qualifiers !== 'object' || Array.isArray(qualifiers))
        return {};
    const out = {};
    for (const name of Object.keys(qualifiers).sort()) {
        const value = qualifiers[name];
        if (typeof value === 'string' && value !== '')
            out[name] = value;
    }
    return out;
}
/**
 * Clé qualifiée normalisée : la valeur, puis `;nom=valeur` par qualifiant non vide, noms triés,
 * valeurs encodées pour-cent (un `;` ou un `=` d'un lot ne casse pas la clé). Sans qualifiant, la
 * clé vaut la valeur : la clé des identifiants 1.0.0 est inchangée.
 */
export function qualifiedKey(value, qualifiers) {
    const normalized = normalizeQualifiers(qualifiers);
    return [value, ...Object.entries(normalized).map(([name, entry]) => `${name}=${encodeURIComponent(entry)}`)].join(';');
}
/**
 * Qualifiants admis pour un type d'identifiant. GS1 : noms et combinaison de la séquence `dlpkey`
 * de la clé, valeurs en CSET 82 de la longueur du Syntax Dictionary. Autre schéma : noms libres en
 * snake_case. Rend la liste des défauts, vide si tout est admis.
 */
export function qualifierIssues(identifierType, qualifiers) {
    const normalized = normalizeQualifiers(qualifiers);
    const names = Object.keys(normalized);
    const issues = [];
    for (const name of names)
        if (!NAME.test(name))
            issues.push(`Nom de qualifiant invalide : « ${name} ».`);
    const sequences = GS1_QUALIFIER_SEQUENCES[identifierType];
    if (sequences === undefined)
        return issues;
    if (names.length > 0 && !sequences.some((sequence) => names.every((name) => sequence.includes(name)))) {
        issues.push(`Les qualifiants ${names.join(', ')} ne forment pas une combinaison admise pour ${identifierType}.`);
    }
    for (const [name, value] of Object.entries(normalized)) {
        if (name === 'uic_ext') {
            if (!/^[0-9][!"%&'()*+,\-./0-9:;<=>?A-Z_a-z]{3}$/.test(value))
                issues.push(`uic_ext : un chiffre puis trois caractères CSET 82.`);
            continue;
        }
        const max = MAX_LENGTH[name];
        if (max !== undefined && (value.length > max || !CSET82.test(value)))
            issues.push(`${name} : ${max} caractères CSET 82 au plus.`);
    }
    return issues;
}
/**
 * Valeurs candidates d'un GTIN reçu sur 14 chiffres : la forme enregistrée peut être un GTIN-8,
 * -12 ou -13 (zéros de tête supprimés). Pour les autres clés, la valeur seule.
 */
export function storedValueCandidates(identifierType, value) {
    if (identifierType !== 'gtin' || value.length !== 14)
        return [value];
    const out = [value];
    for (const length of [13, 12, 8]) {
        const prefix = value.slice(0, 14 - length);
        if (/^0+$/.test(prefix))
            out.push(value.slice(14 - length));
    }
    return out;
}
//# sourceMappingURL=qualified-key.js.map